import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'react-use';
import { yupResolver } from '@hookform/resolvers/yup';
import { DialogContent, FormControl, FormHelperText, MenuItem, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { AxiosError } from 'axios';
import { StatusCodes } from 'http-status-codes';
import { every, first, isNil, some } from 'lodash';
import { ref, type InferType } from 'yup';

import type { DrugFormData } from '@/api/domains/drugs.api';
import { DrugsQueryKeys } from '@/api/domains/drugs.query-keys';
import { useGlobalErrorStore } from '@/components/dialogs';
import { CustomFormLabel, FormProvider, RHFSelect, RHFTextField } from '@/components/form-controls';
import { ModalBody } from '@/components/Modal';
import {
  NomenclatureConfirmationEditDialog,
  useNomenclatureContextWindows,
  useNomenclatureStoreActions,
  useNomenclatureStorePreferredTab,
} from '@/components/nomenclature';
import type { RecordStatusId } from '@/shared/constants/record-status.constants';
import { RECORD_STATUS_ACTIVE } from '@/shared/constants/record-status.constants';
import { formatDateTime } from '@/shared/helpers/format.helpers';
import { Yup } from '@/shared/lib/yup';

import { nomenclatureCreateEvent } from '../../events';
import { getRequiredDrugFormData } from '../helpers';
import type { NomenclatureFormProps } from '../NomenclatureForm';
import { NomenclatureModalSidebar } from '../NomenclatureModalSidebar';
import type { NomenclatureModalTab } from '../NomenclatureModalTabbar';
import type { NomenclatureModalVariant } from '../types';
import { useUpdateDrug } from '../useUpdateDrug';
import { NOMENCLATURE_TYPES } from './constants';
import {
  ActiveSubstancesFields,
  BrandField,
  CategoryDictionaryField,
  emptyActiveSubstance,
  MarketingOrganisationField,
  RegistrationFields,
} from './Fields';
import { useDosages } from './useDosages';
import { useUpdateDosages } from './useUpdateDosages';

export type ExpertDataFormProps = {
  onClose: VoidFunction;
  drug: NomenclatureFormProps['drug'];
  variant: NomenclatureModalVariant | null;
};

type FormSchema = InferType<typeof formSchema> & {
  record_status_id?: RecordStatusId;
};

const nomenclatureTypeOptions = Object.values(NOMENCLATURE_TYPES);

const formSchema = Yup.object().shape({
  categoryDictionary: Yup.object().shape({ id: Yup.number().required() }).nullable(),
  marketingOrg: Yup.object().shape({ id: Yup.number().required() }).nullable(),
  brand: Yup.string().trim(),
  regNumber: Yup.string(),
  startRegDate: Yup.date().nullable().typeError('Введите корректную дату'),
  endRegDate: Yup.date()
    .nullable()
    .typeError('Введите корректную дату')
    .min(ref('startRegDate'), 'Дата меньше начальной'),
  activeSubstances: Yup.array()
    .of(
      Yup.object().shape({
        dosage: Yup.number()
          .nullable()
          .transform(value => (Number.isNaN(value) ? null : value)),
        measure_unit: Yup.object().shape({ id: Yup.number() }).nullable(),
        inn: Yup.object().shape({ id: Yup.number() }).nullable(),
      }),
    )
    .test(
      'isSubstancesRequirementFieldsFilled',
      'Необходимо заполнить все поля действующих веществ',
      (activeSubstances, { createError }) => {
        if (!activeSubstances) {
          return true;
        }

        if (activeSubstances.length === 1) {
          const singleActiveSubstance = first(activeSubstances);
          const isAllFieldsEmpty = every(singleActiveSubstance, isNil);
          const isAllFieldsFilled = every(singleActiveSubstance, value => !isNil(value));

          if (isAllFieldsFilled || isAllFieldsEmpty) {
            return true;
          }

          const isSomeFieldsFilled = some(singleActiveSubstance, value => !isNil(value));
          if (isSomeFieldsFilled) {
            return createError({
              message: 'Необходимо заполнить все поля действующего вещества',
            });
          }
        }

        const isSomeFieldsEmpty = activeSubstances.some(activeSubstance => some(activeSubstance, isNil));

        return !isSomeFieldsEmpty;
      },
    )
    .optional(),
  brand_id: Yup.number().nullable(),
  type: Yup.number().nullable(),
});

const selfTabName: NomenclatureModalTab = 'expertData';

export const ExpertDataForm = (props: ExpertDataFormProps) => {
  const { drug, onClose, variant } = props;
  const { t } = useTranslation('nomenclature-page');

  const preferredTab = useNomenclatureStorePreferredTab();
  const isEditMode = variant === 'edit';
  const isEditAfterCreationMode = variant === 'editAfterCreation';

  const { setGlobalError } = useGlobalErrorStore();

  const updateDrugMutation = useUpdateDrug();
  const updateDosagesMutation = useUpdateDosages(drug?.id);
  const { dosages: activeSubstances } = useDosages({ drugId: drug?.id });

  const { setSelectedRow } = useNomenclatureStoreActions();
  const queryClient = useQueryClient();

  const {
    category_dictionary,
    brand,
    registration_number,
    marketing_org,
    registration_start_date,
    registration_end_date,
    brand_id,
    type,
  } = drug ?? {};

  const methods = useForm<FormSchema>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      categoryDictionary: category_dictionary,
      marketingOrg: marketing_org,
      brand: brand,
      regNumber: registration_number,
      startRegDate: registration_start_date ? new Date(registration_start_date) : null,
      endRegDate: registration_end_date ? new Date(registration_end_date) : null,
      activeSubstances: activeSubstances && activeSubstances.length > 0 ? activeSubstances : [emptyActiveSubstance],
      record_status_id: drug?.record_status_id ?? RECORD_STATUS_ACTIVE,
      brand_id: brand_id ?? null,
      type: type ?? null,
    },
  });

  const {
    formState: { isSubmitting, isDirty, errors, isValid, dirtyFields },
    handleSubmit: wrapHandleSubmit,
    clearErrors,
    setValue,
  } = methods;

  useEffect(() => {
    if (activeSubstances && activeSubstances.length > 0) {
      setValue('activeSubstances', activeSubstances, { shouldDirty: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubstances]);

  const { closeConfirmationCreateDialog, closeConfirmationEditDialog, openConfirmationEditDialog, setActiveTab } =
    useNomenclatureStoreActions();
  const { isConfirmationCreateDialogVisible, isConfirmationEditDialogVisible } = useNomenclatureContextWindows();

  useUpdateEffect(() => {
    if (preferredTab === selfTabName) {
      return;
    }

    if (isDirty) {
      openConfirmationEditDialog();
    } else {
      setActiveTab(preferredTab);
    }
  }, [preferredTab]);

  useEffect(() => {
    if (isValid) {
      clearErrors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  const handleSubmit: SubmitHandler<FormSchema> = async formData => {
    const {
      brand,
      regNumber,
      startRegDate,
      endRegDate,
      categoryDictionary,
      activeSubstances,
      record_status_id,
      marketingOrg,
      brand_id,
      type,
    } = formData;

    if (!drug) {
      return;
    }

    try {
      if (drug && drug.maker && isDirty) {
        const commonData = getRequiredDrugFormData(drug);
        const preparedFormData: DrugFormData = {
          ...commonData,
          brand,
          record_status_id,
          registration_number: regNumber,
          category_dictionary_id: categoryDictionary?.id ?? 0,
          registration_start_date: startRegDate ? formatDateTime(startRegDate, 'yyyy-MM-dd') : undefined,
          registration_end_date: endRegDate ? formatDateTime(endRegDate, 'yyyy-MM-dd') : undefined,
          marketing_org_id: marketingOrg?.id ?? 0,
          brand_id: brand_id ?? undefined,
          type: type ?? undefined,
        };

        const updatedDrug = await updateDrugMutation.mutateAsync({
          id: drug.id,
          drugData: preparedFormData,
        });
        setSelectedRow(updatedDrug);
        queryClient.invalidateQueries(DrugsQueryKeys.refreshCurrentSearch());

        setSelectedRow(updatedDrug);

        if (activeSubstances && dirtyFields.activeSubstances) {
          const preparedDosages = activeSubstances.map((activeSubstance, index) => ({
            position: index,
            inn_id: activeSubstance.inn?.id ?? 0,
            measure_unit_id: activeSubstance.measure_unit?.id ?? 0,
            dosage: activeSubstance.dosage ?? 0,
          }));

          const isEmptyActiveSubstances = activeSubstances.length === 1 && !activeSubstances[0].dosage;
          if (isEmptyActiveSubstances) {
            await updateDosagesMutation.mutateAsync({ dosages: [] });
          } else {
            await updateDosagesMutation.mutateAsync({ dosages: preparedDosages });
          }
        }
      }

      if (isEditAfterCreationMode) {
        if (drug) {
          nomenclatureCreateEvent.emit(drug);
        }
      }

      if (isConfirmationCreateDialogVisible) {
        closeConfirmationCreateDialog();
      }

      if (isConfirmationEditDialogVisible) {
        closeConfirmationEditDialog();
      }

      if (preferredTab !== selfTabName) {
        setActiveTab(preferredTab);
      }

      if (preferredTab === selfTabName) {
        onClose();
      }
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === StatusCodes.BAD_REQUEST) {
        const customError = new Error(t('modals.form.validation.fill-release-form'));
        setGlobalError(customError);
      } else {
        setGlobalError(error);
      }
    }
  };

  const handleNomenclatureConfirmationEditDialogClose = () => {
    if (preferredTab === selfTabName) {
      onClose();
    } else {
      setActiveTab(preferredTab);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit}>
      <ModalBody
        hasTabbar
        sidebar={
          <NomenclatureModalSidebar drug={drug} isEditMode={isEditAfterCreationMode || isEditMode} onCancel={onClose} />
        }
      >
        <DialogContent sx={{ paddingY: 3 }}>
          <Typography variant="subtitle1" sx={{ marginBottom: 2, fontWeight: 700, letterSpacing: '0.15px' }}>
            {t('tabs.expert-data')}
          </Typography>

          <Grid container rowSpacing={2}>
            <Grid container columnSpacing={2} rowSpacing={3}>
              <Grid xs={6}>
                <CategoryDictionaryField />
              </Grid>

              <Grid xs={6}>
                <MarketingOrganisationField />
              </Grid>

              <Grid xs={12} columnSpacing={0}>
                <ActiveSubstancesFields drug={drug} />
                {errors.activeSubstances && (
                  <FormHelperText error>
                    {errors.activeSubstances && errors.activeSubstances.root?.message}
                  </FormHelperText>
                )}
              </Grid>

              <Grid container rowSpacing={2} xs={12}>
                <Grid xs={8.5}>
                  <FormControl fullWidth>
                    <CustomFormLabel htmlFor="brand">{t('modals.form.ui.expert-data.brand')}</CustomFormLabel>
                    <RHFTextField fullWidth variant="filled" name="brand" id="brand" />
                  </FormControl>
                </Grid>
                <Grid xs={3}>
                  <FormControl fullWidth>
                    <CustomFormLabel htmlFor="regNumber">
                      {t('modals.form.ui.expert-data.registration-number')}
                    </CustomFormLabel>
                    <RHFTextField fullWidth variant="filled" name="regNumber" id="regNumber" />
                  </FormControl>
                </Grid>
              </Grid>

              <Grid xs={12}>
                <RegistrationFields />
              </Grid>
            </Grid>
          </Grid>

          <Typography
            variant="subtitle1"
            sx={{ marginBottom: 3, marginTop: 3, fontWeight: 700, letterSpacing: '0.15px' }}
          >
            {t('tabs.beauty')}
          </Typography>

          <Grid container spacing={2}>
            <Grid xs={8}>
              <BrandField />
            </Grid>

            <Grid xs={4}>
              <FormControl fullWidth>
                <CustomFormLabel htmlFor="type">{t('modals.form.ui.expert-data.nomenclature-type')}</CustomFormLabel>
                <RHFSelect fullWidth name="type" id="type" variant="filled">
                  {nomenclatureTypeOptions.map(type => (
                    <MenuItem value={type.id} key={type.id}>
                      {type.label}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
      </ModalBody>

      <NomenclatureConfirmationEditDialog
        isLoading={isSubmitting}
        onProceed={wrapHandleSubmit(handleSubmit)}
        onClose={handleNomenclatureConfirmationEditDialogClose}
      />
    </FormProvider>
  );
};
